import { useState, useEffect } from 'react';
import { getApp } from 'firebase/app';
import {
  getFirestore, collection, doc, getDoc, updateDoc,
  onSnapshot, query, orderBy, serverTimestamp,
} from 'firebase/firestore';
import { pushNotificationEvent } from '../../../utils/notificationRouter';

const MONO = "'Share Tech Mono', monospace";
const DB   = () => getFirestore(getApp());

const LEAVE_LABELS = {
  casual:   { label:'Casual Leave',         short:'CL', color:'#0e9cc0' },
  sick:     { label:'Sick Leave',            short:'SL', color:'#ef4444' },
  earned:   { label:'Earned Leave',          short:'EL', color:'#10b981' },
  comp_off: { label:'Compensatory Off',      short:'CO', color:'#6366f1' },
  unpaid:   { label:'Unpaid Leave',          short:'UL', color:'#94a3b8' },
  wfh:      { label:'Work From Home',        short:'WF', color:'#f59e0b' },
};

function fmtDate(ts) {
  if (!ts?.toDate) return '—';
  const d = ts.toDate();
  return d.toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' });
}

// Shared by Admin Panel → LEAVE REQUESTS and Attendance → APPROVALS (admin + ops).
export default function LeaveRequestsTab({ reviewerName = 'Admin', reviewerUid = null }) {
  const [requests,  setRequests]  = useState([]);
  const [busy,      setBusy]      = useState({});
  const [notes,     setNotes]     = useState({});
  const [filter,    setFilter]    = useState('pending');

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(DB(), 'leave_requests'), orderBy('appliedAt', 'desc')),
      snap => setRequests(snap.docs.map(d => ({ id:d.id, ...d.data() }))),
      () => {},
    );
    return unsub;
  }, []);

  async function approve(req) {
    setBusy(b => ({ ...b, [req.id]:true }));
    try {
      const db = DB();
      await updateDoc(doc(db, 'leave_requests', req.id), {
        status:       'approved',
        reviewedBy:   reviewerName,
        reviewedByUid: reviewerUid,
        reviewedAt:   serverTimestamp(),
        reviewNote:   notes[req.id]?.trim() || null,
      });
      // Deduct from authorized_users leave balance
      const userRef  = doc(db, 'authorized_users', req.employeeUid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const ud = userSnap.data();
        const lb = ud.leaveBalance || {};
        const lu = ud.leaveUsed    || {};
        const lt = req.leaveType;
        const days = req.totalDays || 0;
        await updateDoc(userRef, {
          [`leaveBalance.${lt}`]: Math.max(0, (lb[lt] || 0) - days),
          [`leaveUsed.${lt}`]:    (lu[lt] || 0) + days,
        });
      }
      await pushNotificationEvent({
        eventType:  'LEAVE_APPROVED',
        sourceId:   req.id, sourceType: 'leave_request',
        payload:    { requestId:req.id, employeeName:req.employeeName, employeeUid:req.employeeUid,
                      leaveType:req.leaveType, fromDate:req.fromDate, toDate:req.toDate,
                      totalDays:req.totalDays, reviewNote: notes[req.id]?.trim() || null },
        actorUid:   reviewerUid || 'admin', actorName: reviewerName,
      });
    } catch(e) { console.error('[LeaveApprove]', e); }
    finally { setBusy(b => ({ ...b, [req.id]:false })); }
  }

  async function reject(req) {
    if (!notes[req.id]?.trim()) {
      setNotes(n => ({ ...n, [req.id]: n[req.id] ?? '' }));
      return;
    }
    setBusy(b => ({ ...b, [req.id]:true }));
    try {
      await updateDoc(doc(DB(), 'leave_requests', req.id), {
        status:     'rejected',
        reviewedBy: reviewerName,
        reviewedByUid: reviewerUid,
        reviewedAt: serverTimestamp(),
        reviewNote: notes[req.id].trim(),
      });
      await pushNotificationEvent({
        eventType:  'LEAVE_REJECTED',
        sourceId:   req.id, sourceType: 'leave_request',
        payload:    { requestId:req.id, employeeName:req.employeeName, employeeUid:req.employeeUid,
                      leaveType:req.leaveType, fromDate:req.fromDate, toDate:req.toDate,
                      totalDays:req.totalDays, reviewNote: notes[req.id].trim() },
        actorUid:   reviewerUid || 'admin', actorName: reviewerName,
      });
    } catch(e) { console.error('[LeaveReject]', e); }
    finally { setBusy(b => ({ ...b, [req.id]:false })); }
  }

  const STATUS_CLR = {
    pending:   { color:'#f59e0b', bg:'#f59e0b18', border:'#f59e0b44' },
    approved:  { color:'#22c55e', bg:'#22c55e18', border:'#22c55e44' },
    rejected:  { color:'#ef4444', bg:'#ef444418', border:'#ef444444' },
    cancelled: { color:'#94a3b8', bg:'#94a3b818', border:'#94a3b844' },
  };

  const filtered = filter === 'all' ? requests : requests.filter(r => r.status === filter);
  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div>
      {/* Filter tabs */}
      <div style={{ display:'flex', gap:6, marginBottom:16, flexWrap:'wrap' }}>
        {[['pending',`PENDING${pendingCount>0?' ('+pendingCount+')':''}`],['approved','APPROVED'],['rejected','REJECTED'],['all','ALL']].map(([v,l]) => (
          <button key={v} onClick={() => setFilter(v)} style={{
            fontFamily:MONO, fontSize:9, padding:'5px 12px', borderRadius:5, cursor:'pointer', letterSpacing:1,
            background: filter===v ? (v==='pending'?'#f59e0b18':v==='approved'?'#22c55e18':v==='rejected'?'#ef444418':'#0e9cc018') : 'transparent',
            border:`1px solid ${filter===v ? (v==='pending'?'#f59e0b55':v==='approved'?'#22c55e55':v==='rejected'?'#ef444455':'#0e9cc055') : '#1e2636'}`,
            color: filter===v ? (v==='pending'?'#f59e0b':v==='approved'?'#22c55e':v==='rejected'?'#ef4444':'#0e9cc0') : '#667',
          }}>{l}</button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div style={{ fontFamily:MONO, fontSize:11, color:'#445566', textAlign:'center', padding:40 }}>
          No {filter === 'all' ? '' : filter} leave requests.
        </div>
      ) : (
        filtered.map(req => {
          const lt = LEAVE_LABELS[req.leaveType] || { label:req.leaveType, short:'?', color:'#94a3b8' };
          const st = STATUS_CLR[req.status]      || STATUS_CLR.pending;
          const isBusy = busy[req.id];
          return (
            <div key={req.id} style={{
              background:'#070a0f', border:`1px solid #1e2636`,
              borderLeft:`3px solid ${lt.color}`,
              borderRadius:'0 8px 8px 0', padding:'12px 16px', marginBottom:10,
            }}>
              {/* Header row */}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <div style={{
                    width:28, height:28, borderRadius:6, flexShrink:0,
                    background:`${lt.color}18`, color:lt.color,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontFamily:MONO, fontSize:9, fontWeight:700,
                  }}>{lt.short}</div>
                  <div>
                    <div style={{ fontFamily:MONO, fontSize:11, fontWeight:700, color:'#dde9f0' }}>
                      {req.employeeName}
                    </div>
                    <div style={{ fontFamily:MONO, fontSize:9, color:'#667' }}>
                      {lt.label}
                      {req.isHalfDay && <span style={{ color:'#94a3b8', marginLeft:4 }}>(half day)</span>}
                    </div>
                  </div>
                </div>
                <span style={{
                  fontFamily:MONO, fontSize:8, padding:'2px 8px', borderRadius:10,
                  background:st.bg, color:st.color, border:`1px solid ${st.border}`, flexShrink:0,
                }}>
                  {req.status.toUpperCase()}
                </span>
              </div>

              {/* Details row */}
              <div style={{ fontFamily:MONO, fontSize:9, color:'#667', marginBottom:6, display:'flex', gap:16, flexWrap:'wrap' }}>
                <span>
                  {req.fromDate === req.toDate ? req.fromDate : `${req.fromDate} → ${req.toDate}`}
                  {' · '}{req.totalDays} day{req.totalDays !== 1 ? 's' : ''}
                </span>
                <span>Applied {fmtDate(req.appliedAt)}</span>
                {req.reviewedAt && <span>Reviewed {fmtDate(req.reviewedAt)}{req.reviewedBy ? ` by ${req.reviewedBy}` : ''}</span>}
              </div>

              {/* Reason */}
              <div style={{ fontFamily:MONO, fontSize:9, color:'#aaa', marginBottom: req.status === 'pending' ? 10 : 0, lineHeight:1.5 }}>
                "{req.reason}"
              </div>

              {/* Review note (if any) */}
              {req.reviewNote && (
                <div style={{ fontFamily:MONO, fontSize:9, color: req.status==='approved' ? '#22c55e' : '#ef4444', marginBottom: req.status==='pending'?10:0 }}>
                  Note: {req.reviewNote}
                </div>
              )}

              {/* Actions — only for pending */}
              {req.status === 'pending' && (
                <div>
                  <input
                    value={notes[req.id] ?? ''}
                    onChange={e => setNotes(n => ({ ...n, [req.id]: e.target.value }))}
                    placeholder="Review note (required for rejection, optional for approval)…"
                    style={{
                      width:'100%', padding:'7px 10px', fontFamily:MONO, fontSize:9,
                      background:'#0d1119', border:'1px solid #1e2636', color:'#dde9f0',
                      borderRadius:5, boxSizing:'border-box', marginBottom:8, outline:'none',
                    }}
                  />
                  <div style={{ display:'flex', gap:8 }}>
                    <button onClick={() => approve(req)} disabled={isBusy} style={{
                      flex:1, fontFamily:MONO, fontSize:9, letterSpacing:1, fontWeight:700,
                      background:'#22c55e18', border:'1px solid #22c55e55', color:'#22c55e',
                      padding:'7px 0', borderRadius:5, cursor: isBusy ? 'wait' : 'pointer', opacity: isBusy ? 0.6 : 1,
                    }}>
                      {isBusy ? 'PROCESSING…' : '✓ APPROVE'}
                    </button>
                    <button onClick={() => reject(req)} disabled={isBusy} style={{
                      flex:1, fontFamily:MONO, fontSize:9, letterSpacing:1,
                      background:'#ef444418', border:'1px solid #ef444455', color:'#ef4444',
                      padding:'7px 0', borderRadius:5, cursor: isBusy ? 'wait' : 'pointer', opacity: isBusy ? 0.6 : 1,
                      title:'Add a review note before rejecting',
                    }}>
                      ✗ REJECT
                    </button>
                  </div>
                  {notes[req.id] === '' && (
                    <div style={{ fontFamily:MONO, fontSize:8, color:'#ef4444', marginTop:4 }}>
                      Review note required to reject.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
